import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Mail } from 'lucide-react';
import { toast } from 'sonner';
import MailSendDialog from '../components/MailSendDialog';
import { desktopApi, mailFailureText, mailOutcomeToast, mailStatusText } from '../lib/desktopApi';

const ReportView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [html, setHtml] = useState('');
  const [error, setError] = useState('');
  const [mail, setMail] = useState(null);
  const [mailPreview, setMailPreview] = useState(null);
  const [mailSending, setMailSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    desktopApi.getReportHtml(reportId)
      .then((page) => {
        if (!cancelled) setHtml(page);
      })
      .catch(() => {
        if (!cancelled) setError('Rapport introuvable.');
      });
    desktopApi.getReportById(reportId)
      .then((report) => {
        if (!cancelled) setMail(report?.mail || null);
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const handleDownload = async () => {
    try {
      const { blob, filename } = await desktopApi.getReportDocument(reportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `rapport_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success('Rapport téléchargé');
    } catch (downloadError) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const openMailDialog = async () => {
    try {
      const preview = await desktopApi.previewReportMail(reportId);
      setMailPreview(preview);
    } catch (sendError) {
      const detail = sendError?.response?.data?.detail;
      toast.error(mailFailureText(detail), { duration: 12000 });
    }
  };

  const confirmMail = async () => {
    setMailSending(true);
    try {
      const result = await desktopApi.sendReportMail(reportId);
      mailOutcomeToast(toast, result?.mail);
      setMail(result?.mail ? { ...result.mail, enabled: mail?.enabled !== false } : mail);
      setMailPreview(null);
      const fresh = await desktopApi.getReportById(reportId).catch(() => null);
      if (fresh?.mail) setMail(fresh.mail);
    } catch (sendError) {
      const detail = sendError?.response?.data?.detail;
      toast.error(mailFailureText(detail), { duration: 12000 });
    } finally {
      setMailSending(false);
    }
  };

  return (
    <div className="safe-screen min-h-screen bg-slate-100 pb-[calc(8.5rem+env(safe-area-inset-bottom,0px))] md:pb-16">
      <div className="bg-background border-b border-border px-4 py-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/historique')}
          data-testid="report-back"
          className="flex items-center gap-2 text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4" />
          Historique
        </button>
        <h1 className="text-sm font-semibold uppercase tracking-wide">Rapport</h1>
        <div className="flex flex-wrap gap-2 w-full sm:ml-auto sm:w-auto">
          {mail?.enabled !== false && (
          <button
            type="button"
            onClick={openMailDialog}
            data-testid="btn-mail-report"
            className="flex items-center gap-2 h-11 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium"
          >
            <Mail className="w-4 h-4" />
            Envoyer par e-mail
          </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            data-testid="btn-download-report"
            className="flex items-center gap-2 h-11 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            Télécharger le PDF
          </button>
        </div>
        <p className="w-full text-xs text-muted-foreground" data-testid="report-mail-status">{mailStatusText(mail)}</p>
      </div>
      <MailSendDialog
        open={!!mailPreview}
        preview={mailPreview}
        sending={mailSending}
        onCancel={() => { if (!mailSending) setMailPreview(null); }}
        onConfirm={confirmMail}
      />
      {error ? (
        <p className="p-8 text-center text-muted-foreground" data-testid="report-error">{error}</p>
      ) : (
        <iframe
          title="Rapport de nuit"
          data-testid="report-frame"
          srcDoc={html}
          sandbox=""
          className="w-full border-0 bg-white"
          style={{ minHeight: 'calc(100vh - 8rem)' }}
        />
      )}
    </div>
  );
};

export default ReportView;
